import "./listView.scss";

import { List, Map } from "immutable";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { createRef, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import keyConstants from "../../constants/keyConstants";
import { isChildInParentView } from "../../utils/domUtils";
import { withPopoverRepositionContext } from "../popover/PopoverRepositionContext";

class ListView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedIndex: props.selectedItemIndex
    };

    this._domNode = createRef();
    this._itemsRefMap = Map();

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onFocus = this.onFocus.bind(this);
  }

  componentDidMount() {
    if (this._domNode && this._domNode.current) {
      if (this.props.getDom) {
        this.props.getDom(this._domNode.current);
      }
      //#!if iOS !== true
      if (this.props.autoFocus) {
        this._domNode.current.focus();
      }
      //#!endif
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectedItemIndex !== prevProps.selectedItemIndex) {
      this.updateSelectedIndex(this.props.selectedItemIndex);
    }

    if (this.props.repositionPopover) {
      this.props.repositionPopover();
    }
  }

  updateSelectedIndex(newIndex) {
    this.setState({ selectedIndex: newIndex });
  }

  getItemsCount() {
    const { items } = this.props;
    return List.isList(items) ? items.size : items.length;
  }

  scrollToItem(itemIndex) {
    const itemRef = this._itemsRefMap.get(itemIndex);
    const { _domNode } = this;
    if (
      itemRef &&
      itemRef.current &&
      _domNode &&
      _domNode.current &&
      !isChildInParentView(_domNode.current, itemRef.current)
    ) {
      itemRef.current.scrollIntoView({
        behavior: "smooth"
      });
    }
  }

  onItemHighlight(newIndex) {
    const { onItemHighlight } = this.props;
    if (onItemHighlight) {
      onItemHighlight(newIndex);
    }
  }

  onKeyDown(e) {
    const { onItemSelect, isEnterToSelect, isHorizontal } = this.props;
    const { selectedIndex } = this.state;
    const nextKey = isHorizontal ? keyConstants.Right : keyConstants.Down;
    const prevKey = isHorizontal ? keyConstants.Left : keyConstants.Up;
    if (e.key === nextKey || e.key === prevKey) {
      e.stopPropagation();
      e.preventDefault();
      let nextItemIndex = selectedIndex;
      if (e.key === nextKey && nextItemIndex < this.getItemsCount() - 1) {
        nextItemIndex += 1;
      } else if (e.key === prevKey && nextItemIndex > 0) {
        nextItemIndex -= 1;
      }
      if (nextItemIndex !== selectedIndex) {
        this.setState({ selectedIndex: nextItemIndex });
        this.scrollToItem(nextItemIndex);
        this.onItemHighlight(nextItemIndex);
        if (!isEnterToSelect) {
          onItemSelect(nextItemIndex);
        }
      }
    } else if (e.key === keyConstants.Enter && selectedIndex >= 0 && selectedIndex < this.getItemsCount()) {
      e.stopPropagation();
      e.preventDefault();
      this.onItemHighlight(selectedIndex);
      onItemSelect(selectedIndex);
    }
  }

  onClick(_e) {
    const { _domNode } = this;
    if (_domNode && _domNode.current) {
      _domNode.current.focus();
    }
  }

  getItemRef(itemIndex) {
    let ref = this._itemsRefMap.get(itemIndex);
    if (!ref) {
      ref = createRef();
      this._itemsRefMap = this._itemsRefMap.set(itemIndex, ref);
    }
    return ref;
  }

  getChildren() {
    return this.props.items.map((item, itemIndex) => {
      const ref = this.getItemRef(itemIndex);
      return this.props.itemRenderer(item, itemIndex === this.state.selectedIndex, ref, itemIndex);
    });
  }

  onFocus() {
    if (isNil(this.state.selectedIndex) || this.state.selectedIndex < 0) {
      this.updateSelectedIndex(0);
    }
  }

  render() {
    let classList = ["listView"];
    if (this.props.className) {
      classList.push(this.props.className);
    }
    if (this.props.disabled) {
      classList.push("disabled");
    }
    return (
      <div
        ref={this._domNode}
        className={classList.join(" ")}
        onKeyDown={this.props.onItemSelect && !this.props.disabled ? this.onKeyDown : null}
        tabIndex={0}
        onClick={!this.props.disabled ? this.onClick : null}
        onFocus={!this.props.disabled ? this.onFocus : null}
      >
        {this.getChildren()}
      </div>
    );
  }
}

ListView.displayName = "listView";

ListView.defaultProps = {
  isEnterToSelect: true
};

ListView.propTypes = {
  disabled: PropTypes.bool,
  items: PropTypes.oneOfType([PropTypes.array, ImmutablePropTypes.list]).isRequired,
  selectedItemIndex: PropTypes.number,
  onItemSelect: PropTypes.func.isRequired,
  onItemHighlight: PropTypes.func,
  isEnterToSelect: PropTypes.bool.isRequired,
  itemRenderer: PropTypes.func.isRequired,
  getDom: PropTypes.func,
  className: PropTypes.string,
  autoFocus: PropTypes.bool,
  isHorizontal: PropTypes.bool,
  repositionPopover: PropTypes.func
};

export default React.memo(withPopoverRepositionContext(ListView));
